import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ButtonBase, Skeleton } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import { UI, formatDateTime } from "./format";

/*
 * Entry point to a file's notifications on the ocean / air / road shipment page:
 * a status strip with what needs attention (open alerts, customer messages
 * waiting for approval), the rules in force and the last notification.
 */

const MODE_TEXT = {
    OCEAN: "Konteyner olayları, ETA ve serbest süre takibi",
    AIR: "Uçuş, ETA, teslim ve POD takibi",
    ROAD: "Araç hareketi, ETA, teslim ve POD takibi",
};

const StatusPill = ({ tone, children }) => (
    <MDBox
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.25,
            borderRadius: "999px",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.7,
            whiteSpace: "nowrap",
            color: UI.tone[tone].fg,
            backgroundColor: UI.tone[tone].bg,
            border: `1px solid ${UI.tone[tone].border}`,
        }}
    >
        <MDBox component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: UI.tone[tone].fg }} />
        {children}
    </MDBox>
);

StatusPill.propTypes = { tone: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const Metric = ({ label, value, tone }) => (
    <MDBox sx={{ minWidth: 0, px: { xs: 1.25, sm: 2 }, py: 0.25, "&:not(:first-of-type)": { borderLeft: `1px solid ${UI.border}` } }}>
        <MDTypography
            variant="caption"
            display="block"
            sx={{ color: UI.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}
        >
            {label}
        </MDTypography>
        <MDTypography
            variant="button"
            display="block"
            sx={{ color: tone ? UI.tone[tone].fg : UI.text, fontSize: 15, fontWeight: 700, lineHeight: 1.5, whiteSpace: "nowrap" }}
        >
            {value}
        </MDTypography>
    </MDBox>
);

Metric.defaultProps = { tone: null };
Metric.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, tone: PropTypes.string };

export default function NotificationsLauncher({ shipmentId, mode, refreshKey, onOpen }) {
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        let alive = true;
        CrudService.getShipmentNotifications(shipmentId, mode)
            .then((response) => {
                if (!alive) return;
                const d = response.data;
                const last = d.history.find((l) => l.status !== "PENDING_APPROVAL") || null;
                setSummary({
                    alerts: d.openAlerts.length,
                    high: d.openAlerts.filter((a) => a.priority === "HIGH").length,
                    pending: d.history.filter((l) => l.status === "PENDING_APPROVAL").length,
                    activeRules: d.settings.rows.filter((r) => r.enabled !== false).length,
                    totalRules: d.settings.rows.length,
                    customerRules: d.settings.rows.filter((r) => r.enabled !== false && r.notifyCustomer).length,
                    lastAt: last ? last.sentAt || last.created_at : null,
                });
            })
            .catch(() => alive && setSummary({ error: true }));
        return () => {
            alive = false;
        };
    }, [shipmentId, mode, refreshKey]);

    const loading = !summary;
    const attention = summary && !summary.error && (summary.alerts > 0 || summary.pending > 0);
    let accent = "success";
    if (summary?.error) accent = "neutral";
    else if (summary?.high) accent = "danger";
    else if (attention) accent = "warning";

    let status = "Aksiyon gerekmiyor";
    if (summary?.error) status = "Özet yüklenemedi";
    else if (summary?.high) status = `${summary.high} acil uyarı`;
    else if (summary?.alerts) status = `${summary.alerts} açık uyarı`;
    else if (summary?.pending) status = `${summary.pending} onay bekliyor`;

    const Icon = attention ? NotificationsActiveOutlinedIcon : NotificationsNoneOutlinedIcon;

    return (
        <ButtonBase
            onClick={onOpen}
            aria-label="Bildirimleri aç"
            sx={{
                mt: 2,
                width: "100%",
                maxWidth: 920,
                display: "block",
                textAlign: "left",
                borderRadius: "14px",
                border: `1px solid ${attention ? UI.tone[accent].border : UI.border}`,
                backgroundColor: "#fff",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                transition: "border-color .15s, box-shadow .15s, transform .15s",
                "&:hover": { borderColor: "#94a3b8", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)" },
                "&:hover .launcher-cta": { color: UI.tone.info.fg },
                "&:hover .launcher-cta svg": { transform: "translateX(3px)" },
            }}
        >
            <MDBox
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    rowGap: 1.5,
                    px: 2.25,
                    py: 1.75,
                    borderLeft: `4px solid ${accent === "neutral" ? UI.border : UI.tone[accent].fg}`,
                }}
            >
                <MDBox display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: "1 1 260px" }}>
                    <MDBox
                        sx={{
                            width: 42,
                            height: 42,
                            flexShrink: 0,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: UI.tone[accent].fg,
                            backgroundColor: UI.tone[accent].bg,
                            border: `1px solid ${UI.tone[accent].border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                    <MDBox minWidth={0}>
                        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <MDTypography variant="h6" sx={{ color: UI.text, fontSize: 16, lineHeight: 1.3 }}>
                                Bildirimler
                            </MDTypography>
                            {loading ? <Skeleton variant="rounded" width={110} height={22} /> : <StatusPill tone={accent}>{status}</StatusPill>}
                        </MDBox>
                        <MDTypography variant="caption" display="block" sx={{ color: UI.muted, lineHeight: 1.5, mt: 0.25 }}>
                            {summary?.error ? "Açmak için tıklayın" : MODE_TEXT[mode]}
                        </MDTypography>
                    </MDBox>
                </MDBox>

                {!summary?.error && (
                    <MDBox display="flex" alignItems="center" sx={{ flexShrink: 0, ml: { xs: -1.25, sm: 0 } }}>
                        {loading ? (
                            <Skeleton variant="rounded" width={300} height={38} />
                        ) : (
                            <>
                                <Metric label="Açık uyarı" value={summary.alerts} tone={summary.high ? "danger" : summary.alerts ? "warning" : null} />
                                <Metric label="Onay bekleyen" value={summary.pending} tone={summary.pending ? "warning" : null} />
                                <Metric
                                    label="Aktif kural"
                                    value={
                                        <>
                                            {summary.activeRules}
                                            <MDBox component="span" sx={{ color: UI.muted, fontWeight: 500 }}>
                                                /{summary.totalRules}
                                            </MDBox>
                                        </>
                                    }
                                />
                                <Metric label="Son bildirim" value={summary.lastAt ? formatDateTime(summary.lastAt) : "—"} />
                            </>
                        )}
                    </MDBox>
                )}

                <MDBox
                    className="launcher-cta"
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                        ml: "auto",
                        pl: { md: 1 },
                        flexShrink: 0,
                        color: UI.text,
                        fontSize: 13,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        transition: "color .15s",
                        "& svg": { transition: "transform .15s" },
                    }}
                >
                    Paneli aç
                    <ArrowForwardRoundedIcon sx={{ fontSize: "18px !important" }} />
                </MDBox>
            </MDBox>
            {summary?.customerRules > 0 && (
                <MDBox px={2.25} py={0.75} sx={{ borderTop: `1px solid ${UI.border}`, backgroundColor: UI.subtle, borderLeft: `4px solid ${UI.border}` }}>
                    <MDTypography variant="caption" sx={{ color: UI.muted }}>
                        {summary.customerRules} kural müşteriye de bildirim gönderiyor
                    </MDTypography>
                </MDBox>
            )}
        </ButtonBase>
    );
}

NotificationsLauncher.defaultProps = { mode: "OCEAN", refreshKey: 0 };

NotificationsLauncher.propTypes = {
    shipmentId: PropTypes.string.isRequired,
    mode: PropTypes.oneOf(["OCEAN", "AIR", "ROAD"]),
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    onOpen: PropTypes.func.isRequired,
};
